import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import {
  Modal,
  Button,
  TextInput,
  Select,
  Label,
  Datepicker,
  ConfirmationAlertModal,
} from 'src/components';
import SearchIcon from 'src/assets/images/Search-Icon.svg';
import CrossIcon from 'src/assets/images/Cross-icon.svg';
import http from 'src/api/endpoints';
import useLoading from 'src/hooks/useLoading';
import { PARENT_URL } from 'src/config/parentURL';
import EditRequirementSkeletonCard from './EditRequirementSkeletonCard';
import { RootState, AppDispatch } from 'src/redux/store';
import { getAllProjects } from 'src/redux/projectSlice';

interface IEditRequirementModal {
  isOpen: boolean;
  onClose: () => void;
  requiredIds: {
    projectId: string;
    staffingId: string;
    project_deadline: string;
  };
}

interface IRole {
  role: string;
  number_of_member: number;
  from: string | number | Date;
  until: string | number | Date;
  type: string;
  staffingRequirementId: string;
}

const DEFAULT_FORM_VALUE = {
  staffingRequirementId: '',
  role: '',
  number_of_member: 1,
  from: '',
  until: '',
  type: '',
};

const EditRequirementModal = ({
  isOpen,
  onClose,
  requiredIds,
}: IEditRequirementModal) => {
  const dispatch: AppDispatch = useDispatch();
  const [defaultRoles, setDefaultRoles] = useState<any>([]);
  const [allRoles, setAllRoles] = useState<any>([]);
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState<
    number | null
  >(null);
  const [formValues, setFormValues] = useState<IRole>(DEFAULT_FORM_VALUE);
  const [searchRole, setSearchRole] = useState<string>('');
  const [isCreationAllowed, setIsCreationAllowed] = useState<boolean | null>(
    null
  );
  const [showErrorModal, setShowErrorModal] = useState<{
    show: boolean;
    message: string;
  }>({
    show: false,
    message: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState<{
    show: boolean;
    index: number | null;
    message: string;
  }>({
    show: false,
    index: null,
    message: '',
  });

  const {
    isLoading: isStaffingRequirementLoading,
    startLoading: staffingRequirementStartLoading,
    stopLoading: staffingRequirementStopLoading,
  } = useLoading();
  const {
    isLoading: isUpdatingStaffingRequirementLoading,
    startLoading: staffingUpdatingRequirementStartLoading,
    stopLoading: staffingUpdatingRequirementStopLoading,
  } = useLoading();
  const {
    filters: { data: filtersData },
  } = useSelector((state: RootState) => state);
  const { filterRoles } = filtersData || {};

  const iterableRolesFilters = useMemo(() => {
    const data = filterRoles?.map((item: any) => ({
      label: item.name,
      value: item.id,
    }));
    return data;
  }, [filterRoles]);

  const getInformationFromIndex = (index: number) => {
    if (index !== -1 && index !== null) {
      const getIndexData = allRoles[index as number];
      return {
        role: getIndexData?.roleId || '',
        from: getIndexData?.fromDate ? new Date(getIndexData.fromDate) : '',
        until: getIndexData?.untilDate ? new Date(getIndexData.untilDate) : '',
        number_of_member: getIndexData?.numberOfRequiredUsers || 1,
        type: getIndexData?.time || '',
        staffingRequirementId: getIndexData?.staffingRequirementId,
      };
    } else {
      return DEFAULT_FORM_VALUE;
    }
  };

  useEffect(() => {
    const data = getInformationFromIndex(currentSelectedIndex as number);
    setFormValues(data);
  }, [currentSelectedIndex]);

  const getStaffingRequirements = async () => {
    try {
      staffingRequirementStartLoading();
      const response = await http.getProjectStaffingRequirements(
        PARENT_URL.PROJECTS,
        requiredIds.projectId
      );
      if (response.message === 'The request is completed successfully') {
        setAllRoles(response?.staffingRequirements);
        setDefaultRoles(response?.staffingRequirements);
        const getIndex = response?.staffingRequirements?.findIndex(
          (requirement: any) =>
            requirement?.staffingRequirementId === requiredIds.staffingId
        );
        setCurrentSelectedIndex(getIndex);
        staffingRequirementStopLoading();
        return;
      }
      toast.error('Failed to fetch Members. Please try again later.');
    } catch (error) {
      console.log(error);
      toast.error('Failed to fetch Members. Please try again later.');
    } finally {
      staffingRequirementStopLoading();
    }
  };

  useEffect(() => {
    getStaffingRequirements();
  }, []);

  const TYPE_BUTTON_CLASSNAMES =
    'w-[75px] h-8 border border-zinc-100 bg-white shadow rounded';
  const TYPE_BUTTON_TEXT_CLASSNAMES = 'text-blue-950 text-sm font-normal';

  const getFullTimeTerms = (value: string) => {
    if (value === 'part_time') {
      return 'Part Time';
    } else if (value === 'full_time') {
      return 'Full Time';
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (searchRole !== '') {
      const searchLower = searchRole.toLowerCase();
      const copyData = [...defaultRoles];
      const results = copyData?.filter((item) =>
        item?.roleName?.toLowerCase()?.includes(searchLower)
      );
      setAllRoles(results);
    } else {
      setAllRoles(defaultRoles);
    }
    setCurrentSelectedIndex(null);
    setFormValues(DEFAULT_FORM_VALUE);
  }, [searchRole]);

  const onHandleAddRole = () => {
    setAllRoles([...allRoles, DEFAULT_FORM_VALUE]);
    setCurrentSelectedIndex(allRoles?.length);
  };

  const onHandleAcceptNumber = (e: any) => {
    const { name, value } = e.target;
    let newVal = value;
    if (name === 'number_of_member') {
      newVal = (value || '').replace(/[^1-9]/g, '');
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: newVal,
    }));
  };

  const isFormEmpty = Object.entries(formValues).some(([key, value]) => {
    if (key === 'staffingRequirementId') {
      return false;
    }
    return value === '';
  });

  const onHandleCreation = () => {
    if (formValues.staffingRequirementId === '' && isCreationAllowed === null) {
      return false;
    }
    if (
      formValues.staffingRequirementId === '' &&
      (isCreationAllowed || !isCreationAllowed)
    ) {
      return true;
    }
    return true;
  };

  const onHandleUpdateRequirement = async (index = null, isDelete = false) => {
    let payload: any = {};
    if (isDelete) {
      const data = getInformationFromIndex(index as any);
      payload = {
        requirementCreationKey: true,
        staffingRequirementId: data.staffingRequirementId,
        numberOfRequiredUsers: 0,
        untilDate: moment(data.until).format('MM-DD-YYYY'),
        fromDate: moment(data.from).format('MM-DD-YYYY'),
        time: data.type,
        roleId: data.role,
      };
    } else {
      payload = {
        requirementCreationKey: onHandleCreation(),
        roleId: formValues.role,
        numberOfRequiredUsers: isDelete ? 0 : formValues.number_of_member,
        untilDate: moment(formValues.until).format('MM-DD-YYYY'),
        fromDate: moment(formValues.from).format('MM-DD-YYYY'),
        time: formValues.type,
      };
    }

    if (formValues?.staffingRequirementId !== '') {
      payload.staffingRequirementId = formValues?.staffingRequirementId;
      try {
        staffingUpdatingRequirementStartLoading();
        const response = await http.updateProjectStaffingRequirements(
          PARENT_URL.PROJECTS,
          requiredIds.projectId,
          payload
        );
        if (
          response.message ===
            'Project Staffing requirement has been successfully updated.' ||
          response.message ===
            'You have successfully deleting the staffing requirement' ||
          response.message ===
            'Project Staffing requirement has been successfully created.'
        ) {
          staffingUpdatingRequirementStartLoading();
          dispatch(getAllProjects());
          onClose();
          //getStaffingRequirements();
          return;
        }
        toast.error(
          'Failed to update staffing requirement. Please try again later.'
        );
      } catch (error: any) {
        console.log(error);
        if (
          error?.response?.data?.message ===
          'Yes! you can create staffing requirement against this requirement'
        ) {
          setIsCreationAllowed(true);
          return;
        }
        if (
          error?.response?.data?.message ===
          'You already have an existing staffing requirement for the role and experience level specified. Are you sure you want to add them again?'
        ) {
          setIsCreationAllowed(false);
          return;
        }
        if (
          error?.response?.data?.message ===
          'Please Un-staff or withdraw the invitation first before changing staffing requirement'
        ) {
          setIsCreationAllowed(null);
          setShowErrorModal({
            show: true,
            message:
              'Please Un-staff or withdraw the invitation first before changing staffing requirement',
          });
          return;
        }
        if (
          error?.response?.data?.message === 'Your Requirement already exist'
        ) {
          setIsCreationAllowed(null);
          setShowErrorModal({
            show: true,
            message: 'This requirement already exists',
          });
          return;
        }
        toast.error(
          error?.response?.data?.message ||
            'Something went wrong. Please try again later.'
        );
      } finally {
        staffingUpdatingRequirementStopLoading();
      }
    } else {
      if (index !== null) {
        allRoles?.splice(index, 1);
      }
    }
  };

  useEffect(() => {
    if (isCreationAllowed) {
      onHandleUpdateRequirement();
      setIsCreationAllowed(null);
    }
  }, [isCreationAllowed]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header
      className="max-w-[1048px] max-h-[700px] w-full bg-white rounded-xl flex flex-col font-SFProDisplay"
    >
      <header className="w-full h-[88px] border-b border-b-[#B7BDC7] px-10">
        <div className="text-blue-950 text-xl font-bold pt-5">Edit Role</div>
      </header>
      <div className="flex-grow flex font-SFProDisplay">
        <div className="w-[43%] h-full bg-[#F7F7F7] pl-10 pr-6 py-7 overflow-y-auto">
          <div className="flex justify-between items-center">
            <p className="text-blue-950 text-lg font-medium">Roles</p>
            <p
              className="text-indigo-600 text-sm font-medium cursor-pointer"
              onClick={onHandleAddRole}
            >
              {isStaffingRequirementLoading ? (
                <Skeleton height={18} width={100} />
              ) : (
                '+ ADD ROLE'
              )}
            </p>
          </div>
          <div className="mt-4">
            {isStaffingRequirementLoading ? (
              <Skeleton
                height={36}
                width={375}
                style={{ borderRadius: '20px' }}
              />
            ) : (
              <TextInput
                variant="normal"
                type="text"
                className="w-full"
                inputFieldClassName="h-9 placeholder:text-[#B1B8C3] pt-2 pl-[38px]"
                placeHolder="Search role by name"
                isClear={false}
                onChange={(e) => setSearchRole(e.target.value)}
                leftIcon={{
                  path: SearchIcon,
                  iconAlt: 'search icon',
                }}
                leftIconClassName="top-[9.5px] left-4"
                value={searchRole}
              />
            )}
          </div>
          <div className="mt-4 h-[420px] overflow-auto">
            {isStaffingRequirementLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <EditRequirementSkeletonCard key={i} />
                ))
              : allRoles?.map((role: any, i: number) => {
                  const fullDate = `${moment(role.fromDate || '').format(
                    'DD MMM'
                  )}-${moment(role.untilDate || '').format('DD MMM,YY')}`;
                  return (
                    <div
                      key={i}
                      className={twMerge(
                        'w-full min-h-[70px] bg-white border border-transparent rounded-md shadow-md py-4 px-4 mb-4 cursor-pointer',
                        i === currentSelectedIndex && 'border-[#5557C9]'
                      )}
                      onClick={() => {
                        setCurrentSelectedIndex(i);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-indigo-600 text-xs font-normal">{`Role ${
                          i + 1
                        }`}</p>
                        <img
                          src={CrossIcon}
                          alt="delete icon"
                          className="h-3 w-3 text-[#5F6D9B] cursor-pointer"
                          onClick={() =>
                            setShowDeleteModal({
                              index: i,
                              message:
                                'Are you sure you want to delete this requirement',
                              show: true,
                            })
                          }
                        />
                      </div>
                      <div className="mt-2 flex gap-2 items-center flex-wrap">
                        <p className="text-blue-950 text-sm font-medium capitalize">
                          {`${role.roleName || 'Role Name'} ${
                            role.numberOfRequiredUsers
                              ? `(${role.numberOfRequiredUsers})`
                              : ''
                          }`}
                        </p>
                        <p className="text-slate-500 text-xs font-medium">
                          {`${getFullTimeTerms(role.time)} ${
                            role.fromDate !== undefined
                              ? `| SE 2 (${fullDate})`
                              : ''
                          } `}
                        </p>
                      </div>
                    </div>
                  );
                })}
          </div>
        </div>
        <div
          className="flex-grow h-full pl-8 pr-10 py-7 overflow-y-auto font-SFProDisplay"
          style={{ height: 'auto' }}
        >
          <p className="text-blue-950 text-lg font-medium ">Role Details</p>
          <div className="mt-[30px]">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="flex gap-6">
                <div className="w-[335px]">
                  <Label isRequired text="Role" htmlFor="role" />
                  <Select
                    options={iterableRolesFilters}
                    value={formValues.role}
                    className="w-full h-8"
                    optionClasses="text-sm text-slate-500 font-SFProDisplay font-medium "
                    placeholder="Select Role"
                    onChange={(id: string | number) => {
                      if (formValues.staffingRequirementId === '') {
                        setFormValues((prevValues) => ({
                          ...prevValues,
                          role: id as string,
                        }));
                      }
                    }}
                    disabled={formValues.staffingRequirementId ? true : false}
                    name="role"
                  />
                </div>
                <TextInput
                  variant="normal"
                  label="Number of Members"
                  type="text"
                  className="w-[204px]"
                  inputFieldClassName="h-9"
                  placeHolder="Enter Number"
                  isClear={false}
                  onChange={onHandleAcceptNumber}
                  required
                  value={formValues.number_of_member}
                  name="number_of_member"
                />
              </div>
              <div className="flex gap-6 mt-1">
                <div className="w-full">
                  <Label isRequired text="From" htmlFor="from" />
                  <Datepicker
                    variant="input"
                    containerClass="w-full bg-white table-filter-date"
                    containerInnerClass="px-1 pt-[-40px] h-8"
                    currentDate={formValues.from}
                    onChange={(date) => {
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        from: date,
                        until: moment(date).isSameOrAfter(
                          formValues.until,
                          'date'
                        )
                          ? ''
                          : formValues.until,
                      }));
                    }}
                    minDate={new Date()}
                    maxDate={new Date(requiredIds.project_deadline)}
                  />
                </div>
                <div className="w-full">
                  <Label isRequired text="Until" htmlFor="until" />
                  <Datepicker
                    variant="input"
                    containerClass="w-full bg-white table-filter-date"
                    containerInnerClass="px-1 pt-[-40px] h-8 hover:border-borderBlue"
                    currentDate={formValues.until}
                    onChange={(date) => {
                      setFormValues((prevValues) => ({
                        ...prevValues,
                        until: date,
                      }));
                    }}
                    minDate={
                      formValues.from !== ''
                        ? new Date(
                            new Date(formValues.from).getTime() +
                              24 * 60 * 60 * 1000
                          )
                        : new Date()
                    }
                    maxDate={new Date(requiredIds.project_deadline)}
                  />
                </div>
              </div>
              <div className="flex mt-5">
                <Label isRequired text="Type" />
              </div>
              <div className="font-SFProDisplay mt-[2px] flex gap-3">
                <button
                  type="button"
                  className={twMerge(
                    TYPE_BUTTON_CLASSNAMES,
                    formValues.type === 'full_time' &&
                      'bg-[#EFEFEF] border-[#C7CDD7]'
                  )}
                  value={formValues.type}
                  onClick={() => {
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      type: 'full_time',
                    }));
                  }}
                  name="type"
                >
                  <p className={TYPE_BUTTON_TEXT_CLASSNAMES}>Full time</p>
                </button>
                <button
                  type="button"
                  className={twMerge(
                    TYPE_BUTTON_CLASSNAMES,
                    formValues.type === 'part_time' &&
                      'bg-[#EFEFEF] border-[#C7CDD7]'
                  )}
                  value={formValues.type}
                  onClick={() => {
                    setFormValues((prevValues) => ({
                      ...prevValues,
                      type: 'part_time',
                    }));
                  }}
                  name="type"
                >
                  <p className={TYPE_BUTTON_TEXT_CLASSNAMES}>Part time</p>
                </button>
              </div>
              {/* {formValues.type === 'part_time' && (
                <div className="mt-5">
                  <TextInput
                    variant="normal"
                    label="Role Capacity"
                    type="text"
                    className="w-[274px]"
                    inputFieldClassName="h-9"
                    placeHolder="0"
                    isClear={false}
                    onChange={() => {}}
                    required
                    rightText="%"
                    rightTextClassName="text-slate-500 text-sm font-normal mt-[-4px] mr-[-16px]"
                  />
                </div>
              )} */}

              <div className="flex mt-5">
                <Label isRequired text="Experience Levels" />
              </div>
              <div className="font-SFProDisplay mt-[2px] flex gap-3">
                <button
                  className={`w-[50.5px] ${TYPE_BUTTON_CLASSNAMES} focus:bg-[#EFEFEF] border-[#C7CDD7]`}
                >
                  <p className={TYPE_BUTTON_TEXT_CLASSNAMES}>SE 1</p>
                </button>
                <button
                  className={`w-[50.5px] ${TYPE_BUTTON_CLASSNAMES} focus:bg-[#EFEFEF] border-[#C7CDD7]`}
                >
                  <p className={TYPE_BUTTON_TEXT_CLASSNAMES}>SE 2</p>
                </button>
                <button
                  className={`w-[50.5px] ${TYPE_BUTTON_CLASSNAMES} focus:bg-[#EFEFEF] border-[#C7CDD7]`}
                >
                  <p className={TYPE_BUTTON_TEXT_CLASSNAMES}>SE 3</p>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <footer className="w-full h-[74px] border-t border-t-[#B7BDC7] px-10 flex justify-between items-center">
        <Button
          variant="secondary"
          size="sm"
          label="Cancel"
          className="block w-[83px] h-8"
          labelClassName="text-lightBlue "
          type="button"
          onClick={onClose}
        />
        {isStaffingRequirementLoading ? (
          <Skeleton height={32} width={96} style={{ borderRadius: '20px' }} />
        ) : (
          <Button
            variant="normal"
            size="sm"
            label="Save"
            className="block w-24 h-8"
            type="button"
            disabled={isFormEmpty}
            onClick={onHandleUpdateRequirement}
            isLoading={isUpdatingStaffingRequirementLoading}
            loaderClassName="h-5"
          />
        )}
      </footer>

      <ConfirmationAlertModal
        isOpen={isCreationAllowed === false}
        onClose={() => {
          setIsCreationAllowed(null);
        }}
        onConfirm={() => {
          setIsCreationAllowed(null);
          onHandleUpdateRequirement();
        }}
        confirmationText="You already have an existing staffing requirement for the role and experience level specified. Are you sure you want to add them again?"
        confirmationModalType=""
        modalContainerClassName="max-w-[420px] max-h-[250px]"
        imageClassName="w-20 h-20"
      />
      <ConfirmationAlertModal
        isOpen={showErrorModal.show}
        onClose={() => {
          setShowErrorModal({
            show: false,
            message: '',
          });
        }}
        onConfirm={() => {}}
        confirmationText={showErrorModal.message}
        confirmationModalType="errorPopup"
        modalContainerClassName="max-w-[420px] max-h-[200px]"
        imageClassName="w-20 h-20"
        confirmationHeaderText="Error"
      />
      <ConfirmationAlertModal
        isOpen={showDeleteModal.show}
        onClose={() => {
          setShowDeleteModal({
            index: null,
            message: '',
            show: false,
          });
        }}
        onConfirm={() => {
          setIsCreationAllowed(null);
          onHandleUpdateRequirement(showDeleteModal.index as any, true);
          setShowDeleteModal({
            index: null,
            message: '',
            show: false,
          });
        }}
        confirmationText={showDeleteModal.message}
        confirmationModalType=""
        modalContainerClassName="max-w-[420px] max-h-[200px]"
        imageClassName="w-20 h-20"
      />
    </Modal>
  );
};

export default EditRequirementModal;
